import React from 'react';
import { healthWithReasons, HEALTH_LABEL } from '../../observability/contract';

// The request paths through the platform, drawn once, with every service box
// coloured by the same health rules as its tile. Deliberately simplified: the
// database and the EFS shards are shown as the data layer rather than wired to
// every service that touches them, because five proxies each drawing a line to
// Aurora hides the paths that matter. The service tiles below carry the same
// information as text, so nothing here is only available visually.
const W = 1120;
const H = 560;
const BOX = { w: 150, h: 46 };

const NODES = [
  // The phone: the telephony service sends each call's request and its audio through the
  // public load balancer, under /bridge/voice/*.
  { id: 'ext-twilio', x: 90, y: 60, label: 'Phone', sub: 'public load balancer', ext: true },
  { id: 'ext-telegram', x: 90, y: 150, label: 'Telegram', ext: true },
  { id: 'ext-website', x: 90, y: 240, label: 'Website', ext: true },
  // The Ross mobile app's backend (e-commerce, /ross). app-bridge polls it like the website.
  { id: 'ext-app', x: 90, y: 330, label: 'Ross App', sub: 'mobile app backend', ext: true },
  { id: 'ext-teams', x: 90, y: 420, label: 'Teams', ext: true },
  { id: 'ext-admin', x: 90, y: 510, label: 'Admin UI', sub: 'public load balancer', ext: true },
  // Level with proxy-llm, so a call's speech runs along the top instead of across the
  // agents. Shown "not deployed" where voice_channel_enabled is off.
  { id: 'voice-bridge', x: 270, y: 60 },
  { id: 'telegram-bridge', x: 270, y: 150 },
  { id: 'website-bridge', x: 270, y: 240 },
  { id: 'app-bridge', x: 270, y: 330 },
  { id: 'teams-bridge', x: 270, y: 420 },
  { id: 'admin-console', x: 270, y: 510 },
  { id: 'proxy-channels', x: 470, y: 190 },
  { id: 'agents', x: 660, y: 190, w: 160, h: 110, label: 'Agents', special: 'agents' },
  { id: 'proxy-llm', x: 860, y: 60 },
  { id: 'proxy-tools', x: 860, y: 145 },
  // The agents' egress proxy: their own connections to the internet (curl, pip, the
  // browser) go through it, one log row per site. Shown "not deployed" where
  // proxy_web_enabled is off.
  { id: 'proxy-web', x: 860, y: 230 },
  { id: 'proxy-crons', x: 860, y: 315 },
  { id: 'ext-llm', x: 1030, y: 60, label: 'LLM providers', sub: 'Bedrock, AI Factory', ext: true },
  { id: 'ext-tools', x: 1030, y: 145, label: 'RossGate, Brave', sub: 'third-party APIs', ext: true },
  { id: 'ext-web', x: 1030, y: 230, label: 'Internet', sub: 'websites, package registries', ext: true },
  { id: 'aurora', x: 470, y: 510, label: 'Aurora', special: 'db' },
  { id: 'control-plane', x: 660, y: 510 },
  { id: 'efs', x: 860, y: 510, label: 'EFS', sub: 'agent workspaces', special: 'efs' },
];

// [from, to, label?, sides?]. Direction is the direction a request travels.
// `sides` pins where an edge leaves / arrives when the automatic choice would
// stack two arrowheads on the same point.
const EDGES = [
  ['ext-twilio', 'voice-bridge'],
  ['ext-telegram', 'telegram-bridge'], ['ext-website', 'website-bridge'], ['ext-app', 'app-bridge'],
  ['ext-teams', 'teams-bridge'],
  ['ext-admin', 'admin-console'],
  // voice-bridge into the top of proxy-channels (the caller's requests to their agent), and
  // straight along the top to proxy-llm: a call's speech to text, its voice and its own
  // model, on the caller's key. Both leave its right side, apart.
  ['voice-bridge', 'proxy-channels', null, { from: 'right', fromOffset: 12, to: 'top', toOffset: -40 }],
  ['voice-bridge', 'proxy-llm', 'speech', { from: 'right', fromOffset: -12, to: 'left', toOffset: -12 }],
  // Four bridges into one box: each arrives at its own point on its left side.
  ['telegram-bridge', 'proxy-channels', null, { to: 'left', toOffset: -18 }],
  ['website-bridge', 'proxy-channels', null, { to: 'left', toOffset: -6 }],
  ['app-bridge', 'proxy-channels', null, { to: 'left', toOffset: 6 }],
  ['teams-bridge', 'proxy-channels', null, { to: 'left', toOffset: 18 }],
  ['proxy-channels', 'agents'],
  // Spread along the agents' right side, so four arrows do not leave from one point.
  ['agents', 'proxy-llm', null, { from: 'right', fromOffset: -36 }],
  ['agents', 'proxy-tools', null, { from: 'right', fromOffset: -12 }],
  ['agents', 'proxy-web', null, { from: 'right', fromOffset: 12 }],
  ['agents', 'proxy-crons', null, { from: 'right', fromOffset: 36 }],
  ['proxy-llm', 'ext-llm'], ['proxy-tools', 'ext-tools'], ['proxy-web', 'ext-web'],
  ['proxy-crons', 'control-plane', 'wakes', { from: 'bottom', to: 'top', toOffset: 48 }],
  ['control-plane', 'agents', 'manages', { from: 'top', to: 'bottom' }],
  ['admin-console', 'aurora'], ['control-plane', 'aurora'], ['control-plane', 'efs'],
];

const sizeOf = (n) => ({ w: n.w || BOX.w, h: n.h || BOX.h });

function sidePoint(n, side, offset = 0) {
  const { w, h } = sizeOf(n);
  if (side === 'top') return { x: n.x + offset, y: n.y - h / 2, v: true };
  if (side === 'bottom') return { x: n.x + offset, y: n.y + h / 2, v: true };
  if (side === 'left') return { x: n.x - w / 2, y: n.y + offset, v: false };
  return { x: n.x + w / 2, y: n.y + offset, v: false };
}

// Leave through the side facing the other node: horizontal unless the other node
// is mostly above or below.
function autoSide(n, toward) {
  const dx = toward.x - n.x;
  const dy = toward.y - n.y;
  if (Math.abs(dy) > Math.abs(dx) * 1.2) return dy > 0 ? 'bottom' : 'top';
  return dx > 0 ? 'right' : 'left';
}

function endpoints(a, b, sides = {}) {
  const p = sidePoint(a, sides.from || autoSide(a, b), sides.fromOffset);
  const q = sidePoint(b, sides.to || autoSide(b, a), sides.toOffset);
  return { p, q };
}

function edgePath({ p, q }) {
  const c1 = p.v ? { x: p.x, y: (p.y + q.y) / 2 } : { x: (p.x + q.x) / 2, y: p.y };
  const c2 = q.v ? { x: q.x, y: (p.y + q.y) / 2 } : { x: (p.x + q.x) / 2, y: q.y };
  return `M${p.x},${p.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${q.x},${q.y}`;
}

export default function ArchitectureMap({ services = [], kubernetes = {}, database = {}, onOpen }) {
  const byId = Object.fromEntries(services.map(s => [s.id, s]));
  const nodeById = Object.fromEntries(NODES.map(n => [n.id, n]));
  const healthOf = (id) => (byId[id] ? healthWithReasons(byId[id]).health : null);

  const subFor = (n) => {
    if (n.special === 'agents') return null;
    if (n.ext) return n.sub || null;
    if (n.special === 'db') {
      if (database.acu_writer != null) return `${database.acu_writer} ACU · ${database.connections} conn`;
      if (database.writer_class && database.writer_class !== 'db.serverless') return `${database.writer_class} · ${database.connections ?? 0} conn`;
      return 'Aurora';
    }
    if (n.sub) return n.sub;
    const s = byId[n.id];
    if (!s) return 'not deployed';
    return `${s.running}/${s.desired} · CPU ${Math.round(s.cpu_pct || 0)}%`;
  };

  return (
    <div className="arch-scroll">
      <svg viewBox={`0 0 ${W} ${H}`} className="arch-svg" role="group" aria-label="Platform architecture map">
        <defs>
          <marker id="arch-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" className="arch-arrowhead" />
          </marker>
        </defs>
        <text x={470} y={H - 6} textAnchor="middle" className="arch-caption">
          proxies and bridges also connect to Aurora directly, all but voice-bridge
        </text>
        {EDGES.map(([from, to, label, sides]) => {
          const ends = endpoints(nodeById[from], nodeById[to], sides);
          const h = healthOf(to) || healthOf(from);
          return (
            <g key={`${from}-${to}`} className={`arch-edge${h ? ` is-${h}` : ''}`}>
              <path d={edgePath(ends)} markerEnd="url(#arch-arrow)" />
              {label && (
                <text x={(ends.p.x + ends.q.x) / 2 + 10} y={(ends.p.y + ends.q.y) / 2 - 6}
                      className="arch-edge-label">{label}</text>
              )}
            </g>
          );
        })}
        {NODES.map((n) => {
          const { w, h } = sizeOf(n);
          const svc = byId[n.id];
          const health = n.ext ? null : n.special ? null : healthOf(n.id);
          const interactive = Boolean(svc && onOpen);
          const cls = ['arch-node', n.ext && 'is-ext', n.special && `is-${n.special}`,
            health && `is-${health}`, !n.ext && !n.special && !svc && 'is-absent', interactive && 'is-link']
            .filter(Boolean).join(' ');
          const open = () => interactive && onOpen(n.id);
          const sub = subFor(n);
          return (
            <g
              key={n.id}
              className={cls}
              transform={`translate(${n.x - w / 2},${n.y - h / 2})`}
              {...(interactive ? {
                role: 'button',
                tabIndex: 0,
                'aria-label': `${n.id}, ${HEALTH_LABEL[health] || 'unknown'}. Open service details`,
                onClick: open,
                onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } },
              } : {})}
            >
              <rect width={w} height={h} rx={10} />
              {health && <circle cx={14} cy={h / 2} r={4.5} className="arch-dot" />}
              {n.special === 'agents' ? (
                <>
                  <text x={w / 2} y={28} textAnchor="middle" className="arch-title">Agents</text>
                  <text x={w / 2} y={50} textAnchor="middle" className="arch-big">{kubernetes.agents_running ?? '—'}</text>
                  <text x={w / 2} y={70} textAnchor="middle" className="arch-sub">running on EKS</text>
                  <text x={w / 2} y={90} textAnchor="middle" className="arch-sub">
                    {`${kubernetes.agents_pending || 0} pending · gVisor`}
                  </text>
                </>
              ) : (
                <>
                  <text x={health ? 26 : w / 2} y={sub ? 20 : h / 2 + 4} textAnchor={health ? 'start' : 'middle'}
                        className="arch-title">{n.label || n.id}</text>
                  {sub && (
                    <text x={health ? 26 : w / 2} y={36} textAnchor={health ? 'start' : 'middle'}
                          className="arch-sub">{sub}</text>
                  )}
                </>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
